import { isMultienum } from 'customer-data-objects/property/PropertyIdentifier';
import { parseMultiEnumValue } from 'customer-data-property-utils/parseMultiEnumValue';
import Raven from 'raven-js';
import { getOwnProperty } from '../utils/getOwnProperty';
import { isComplexCondition, isSimpleCondition } from './types';

// Notably, we allow `null` here compared to the isEmptyValue util in the rest of this package
const isEmptyValue = x => {
  return x === '' || x === undefined || x === null;
};
const isValueEqual = (x, y) => {
  return x === y || isEmptyValue(x) && isEmptyValue(y);
};
const isMultiValueEqual = (xs, ys) => {
  const sortedXs = [...xs].sort();
  const sortedYs = [...ys].sort();
  return sortedXs.length === sortedYs.length && sortedXs.every((val, index) => isValueEqual(val, sortedYs[index]));
};
/**
 * Operator implementations for both single-value and multi-value (multienum) properties.
 *
 * For single-value properties, the actual value is compared directly.
 * For multi-value properties, the actual value is an array of selected values.
 *
 * Note on EQUAL/NOT_EQUAL:
 * - For single values: expected[0] is compared directly to the actual value
 * - For multi values: exact set equality is checked (same values, same count)
 * - The expected values array should contain exactly one element for these operators
 */
const EVALUATORS = {
  EQUAL: {
    single: (expected, actual) => isValueEqual(expected[0], actual),
    multi: (expected, actual) => isMultiValueEqual(expected, actual)
  },
  NOT_EQUAL: {
    single: (expected, actual) => !isValueEqual(expected[0], actual),
    multi: (expected, actual) => !isMultiValueEqual(expected, actual)
  },
  ANY_OF: {
    single: (expected, actual) => expected.some(val => isValueEqual(val, actual)),
    multi: (expected, actual) => expected.some(val => actual.some(val2 => isValueEqual(val, val2)))
  },
  NONE_OF: {
    single: (expected, actual) => !expected.some(val => isValueEqual(val, actual)),
    multi: (expected, actual) => expected.every(val => !actual.some(val2 => isValueEqual(val, val2)))
  },
  IS_KNOWN: {
    single: (_, actual) => !isEmptyValue(actual),
    multi: (_, actual) => actual.length > 0
  },
  IS_NOT_KNOWN: {
    single: (_, actual) => isEmptyValue(actual),
    multi: (_, actual) => actual.length === 0
  }
};
/**
 * Creates a condition evaluator bound to a specific form context.
 * The evaluator can check both simple comparisons and complex nested conditions.
 *
 * @example
 * // Simple usage
 * const evaluator = createConditionEvaluator({
 *   getValue: (name) => formValues[name],
 *   properties: propertyDefinitions
 * });
 *
 * // Evaluate a simple condition
 * evaluator.evaluateCondition({
 *   operator: 'EQUAL',
 *   fieldName: 'status',
 *   fieldValues: ['active']
 * });
 *
 * // Evaluate a complex condition
 * evaluator.evaluateCondition({
 *   operator: 'AND',
 *   conditions: [
 *     { operator: 'EQUAL', fieldName: 'type', fieldValues: ['premium'] },
 *     { operator: 'IS_KNOWN', fieldName: 'payment_method', fieldValues: [] }
 *   ]
 * });
 */
export const createConditionEvaluator = context => {
  function evaluateSimpleCondition(condition) {
    const {
      properties,
      getValue
    } = context;
    const {
      operator,
      fieldName,
      fieldValues
    } = condition;
    const property = getOwnProperty(properties, fieldName);
    // TODO: We should consider this an error, but for backwards compatibility, we need to support it for now.
    // the conditional properties panel does not always pass all properties here, and there is some behavior that
    // relies on the assumed fallback to single-value property handling.
    // if (!property) {
    // Raven.captureException(
    //   new Error(`Property not found in evaluateSimpleCondition: ${fieldName}`)
    // );
    // return false;
    // }

    const actualValue = property && isMultienum(property) ? parseMultiEnumValue(getValue(fieldName)) : getValue(fieldName);
    const evaluator = EVALUATORS[operator];
    if (!evaluator) {
      Raven.captureException(new Error(`Unknown operator in evaluateSimpleCondition: ${operator}`));
      return false;
    }
    if (Array.isArray(actualValue)) {
      return evaluator.multi(fieldValues, actualValue);
    } else {
      return evaluator.single(fieldValues, actualValue);
    }
  }
  function evaluateComplexCondition(condition) {
    const {
      operator,
      conditions
    } = condition;
    const results = conditions.map(evaluateCondition);
    switch (operator) {
      case 'AND':
        // All sub-conditions must be true; true if no sub-conditions are present
        return results.every(Boolean);
      case 'OR':
        // Any sub-condition must be true; false if no sub-conditions are present
        return results.some(Boolean);
      default:
        return false;
    }
  }
  function evaluateCondition(condition) {
    try {
      if (isSimpleCondition(condition)) {
        return evaluateSimpleCondition(condition);
      } else if (isComplexCondition(condition)) {
        return evaluateComplexCondition(condition);
      }
    } catch (error) {
      Raven.captureException(new Error('Unknown condition type in evaluateCondition'));
    }
    return false;
  }
  return {
    evaluateCondition
  };
};